import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { FrequencyType } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Frequency Types',
    description: 'Manage frequency types',
    permissionModuleKey: 'frequency_type',
    route: {
      list: '/frequency_type',
      new: '/frequency_type/new',
      detail: (id: number) => `/frequency_type/${id}`,
      edit: (id: number) => `/frequency_type/${id}/edit`,
    },
    api: {
      list: '/p-frequency-type',
      detail: (id: number) => `/p-frequency-type/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search frequency types...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter name...',
        type: 'text' as const,
        getValue: (item: FrequencyType) => item.name,
        options: [],
      },
    ],
    applyFilter: (items: FrequencyType[], filters: { name: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.name;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Frequency Type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/frequency_type/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: FrequencyType) => {
      window.location.href = `/frequency_type/${item.id}`;
    },
    keyExtractor: (item: FrequencyType) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new frequency type.',
    noSearchResults: 'No frequency types found matching your search criteria.',
    noFilterResults: 'No frequency types match the selected filters.',
  },
  delete: {
    title: 'Delete Frequency Type',
    message: 'Are you sure you want to delete this frequency type? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete frequency type. Please try again.',
    handler: async (id: number) => {
      const { deleteFrequencyType } = await import('../../../services/mutations');
      await deleteFrequencyType(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Frequency Type Details',
    notFoundTitle: 'Frequency Type not found',
    notFoundMessage: 'The frequency type you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Frequency Types',
    defaultTitle: 'Unnamed Frequency Type',
    getTitle: (item: FrequencyType) => {
      return item.name || `Frequency Type #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Frequency Type',
      subtitle: 'Add a new frequency type',
      submitText: 'Create Frequency Type',
    },
    edit: {
      title: 'Edit Frequency Type',
      subtitle: 'Update frequency type information',
      submitText: 'Update Frequency Type',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading frequency type...',
    errorMessage: 'Failed to save frequency type. Please try again.',
    create: async (data: Partial<FrequencyType>) => {
      const { createFrequencyType } = await import('../../../services/mutations');
      return await createFrequencyType(data);
    },
    update: async (id: number, data: Partial<FrequencyType>) => {
      const { updateFrequencyType } = await import('../../../services/mutations');
      return await updateFrequencyType(id, data);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter frequency type name',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Frequency Type Information',
        icon: '📝',
        fields: ['name'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<FrequencyType>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: FrequencyType,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Name',
          value: item.name || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: FrequencyType) => {
  return null;
};

export const getInitialFormData = (): Partial<FrequencyType> => {
  return {
    name: '',
  };
};

export const mapItemToFormData = (item: FrequencyType): Partial<FrequencyType> => {
  return {
    name: item.name || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: FrequencyType,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

